import { useState, useEffect } from 'react';
import { X, Download, Info } from 'lucide-react';
import { ViewerMediaItem } from '../types/api';
import { api } from '../services/api';

/**
 * Shared blob-URL cache: the viewer reuses blobs already fetched (and
 * preloads exactly ONE next item for smooth swiping) instead of
 * re-downloading full-size media on every open.
 */
const mediaCache = new Map<string, string>(); // key -> blob URL
const inflight = new Map<string, Promise<string>>();

function getMediaBlobUrl(key: string): Promise<string> {
  const cached = mediaCache.get(key);
  if (cached) return Promise.resolve(cached);
  const pending = inflight.get(key);
  if (pending) return pending;
  const p = api.getAuthenticatedMediaUrl(key).then((url) => {
    mediaCache.set(key, url);
    inflight.delete(key);
    return url;
  }).catch((e) => { inflight.delete(key); throw e; });
  inflight.set(key, p);
  return p;
}

function preloadMedia(key: string) {
  if (!mediaCache.has(key)) getMediaBlobUrl(key).catch(() => {});
}

// bounded cache: keep ~12 items (current + neighbors)
function trimCache() {
  if (mediaCache.size <= 12) return;
  const it = mediaCache.keys();
  for (let i = 0; i < mediaCache.size - 12; i++) {
    const k = it.next().value as string;
    const url = mediaCache.get(k);
    if (url) URL.revokeObjectURL(url);
    mediaCache.delete(k);
  }
}

interface MediaViewerProps {
  item: ViewerMediaItem | null;
  onClose: () => void;
}

export function MediaViewer({ item, onClose }: MediaViewerProps) {
  const [showInfo, setShowInfo] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [mediaUrl, setMediaUrl] = useState<string>('');

  useEffect(() => {
    let alive = true;
    if (item) {
      setImageLoaded(false);
      setMediaUrl('');
      getMediaBlobUrl(item.key)
        .then((url) => { if (alive) setMediaUrl(url); })
        .catch((error) => console.error('Failed to load media:', error));
      // cache lives at module level — revoked by trimCache(), not per item
    }
    return () => { alive = false; };
  }, [item]);

  /** Preload the next media in the list when provided. */
  useEffect(() => {
    if (item?.nextKey) preloadMedia(item.nextKey);
    trimCache();
  }, [item]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (item) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [item, onClose]);

  if (!item) return null;

  const formatFileSize = (bytes: number): string => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDownload = async () => {
    try {
      const blob = await api.getMediaBlob(item.key);
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = item.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup the blob URL
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const renderMedia = () => {
    // Show loading while media URL is being fetched
    if (!mediaUrl) {
      return (
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          <span className="ml-3 text-white">Loading media...</span>
        </div>
      );
    }
    
    switch (item.type) {
      case 'image':
        return (
          <div className="relative max-w-full max-h-full flex items-center justify-center">
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
              </div>
            )}
            <img
              src={mediaUrl}
              alt={item.name}
              className="max-w-full max-h-full object-contain"
              onLoad={() => setImageLoaded(true)}
            />
          </div>
        );
      case 'video':
        return (
          <video
            controls
            className="max-w-full max-h-full"
            onLoadStart={() => setImageLoaded(true)}
          >
            <source src={mediaUrl} />
            Your browser does not support the video tag.
          </video>
        );
      default:
        return (
          <div className="text-center text-white">
            <p className="mb-4">Preview not available for this file type.</p>
            <button
              onClick={handleDownload}
              className="btn-primary"
            >
              Download File
            </button>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
      {/* Controls */}
      <div className="absolute top-4 right-4 flex items-center space-x-2 z-10">
        <button
          onClick={() => setShowInfo(!showInfo)}
          className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
        >
          <Info className="h-5 w-5" />
        </button>
        <button
          onClick={handleDownload}
          className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
        >
          <Download className="h-5 w-5" />
        </button>
        <button
          onClick={onClose}
          className="p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Media Content */}
      <div className="w-full h-full p-8 flex items-center justify-center">
        {renderMedia()}
      </div>

      {/* Info Panel */}
      {showInfo && (
        <div className="absolute top-16 right-4 bg-black bg-opacity-80 text-white p-4 rounded-lg max-w-xs">
          <h3 className="font-semibold mb-2 truncate">{item.name}</h3>
          <div className="space-y-1 text-sm">
            <p><span className="text-gray-300">Size:</span> {formatFileSize(item.size)}</p>
            <p><span className="text-gray-300">Modified:</span> {new Date(item.modified).toLocaleString()}</p>
            <p><span className="text-gray-300">Type:</span> {item.type}</p>
          </div>
        </div>
      )}

      {/* Click overlay to close */}
      <div 
        className="absolute inset-0 -z-10" 
        onClick={onClose}
      />
    </div>
  );
}